import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { renderMarkdownToNode } from "~/hooks/useMarkdownRenderer";
import type { Document as AppDocument } from "~/interface/document";
import styles from "./index.module.scss";

interface ContentData {
  headers: string[];
  rows: string[][];
}

interface HistoryNonFunctionalRequirementsProps {
  formData: AppDocument[];
}

const decodeString = (str: string): string => {
  try {
    return decodeURIComponent(JSON.parse(`"${str}"`));
  } catch {
    return str;
  }
};

const renderMarkdown = renderMarkdownToNode;

const HistoryNonFunctionalRequirements = ({ formData }: HistoryNonFunctionalRequirementsProps) => {
  const rawContent = formData[0]?.content;

  const { content, isMarkdown, markdownContent } = useMemo(() => {
    if (!rawContent) return { content: null, isMarkdown: false, markdownContent: "" };
    try {
      const parsed = JSON.parse(rawContent as unknown as string);
      if (Array.isArray(parsed?.rows)) {
        return { content: parsed as ContentData, isMarkdown: false, markdownContent: "" };
      }
      // Markdown形式の場合
      return {
        content: null,
        isMarkdown: true,
        markdownContent: rawContent as string,
      };
    } catch {
      // JSONパースに失敗した場合もMarkdown扱い
      return {
        content: null,
        isMarkdown: true,
        markdownContent: rawContent as string,
      };
    }
  }, [rawContent]);

  const columnHelper = createColumnHelper<Record<string, string>>();

  const columns = useMemo(() => {
    if (!content) return [];

    return content.headers.map((header: string) =>
      columnHelper.accessor(header, {
        id: header,
        header: decodeString(header),
        cell: (info) => decodeString(info.getValue()),
      }),
    );
  }, [content, columnHelper]);

  const data = useMemo(() => {
    if (!content) return [];

    return content.rows.map((row: string[]) => {
      const obj: Record<string, string> = {};
      content.headers.forEach((header: string, index: number) => {
        obj[header] = row[index] || "";
      });
      return obj;
    });
  }, [content]);

  const table = useReactTable<Record<string, string>>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isMarkdown) {
    return (
      <div className={`${styles["table-wrapper"]} ${styles["document-content"]}`}>
        <div className={styles["markdown-content"]}>{renderMarkdown(markdownContent || "")}</div>
      </div>
    );
  }

  return (
    <div className={styles["table-wrapper"]}>
      <table className={styles["table"]}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default HistoryNonFunctionalRequirements;
